import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styled from '@emotion/styled';
import { init, identify, Identify } from '@amplitude/analytics-browser';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

import { ThemeProvider } from 'emotion-theming';
import { useSelector } from 'react-redux';
import ErrorBoundaryWrapper from './ErrorBoundaryWrapper';

import AppRoutes from '../../services/Routes/routes';
import mainTheme from '../theme/theme';
import FourOhFourRoute from '../../services/Routes/FourOhFourRoute';

const AppWrapper = ({ flags, ldClient }) => {
  const isAmplitudeInitialized = useRef(false);
  const isContentSquareLoaded = useRef(false);

  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const showMaintenanceModeAlert = useSelector((state) => state.auth.ui.showMaintenanceModeAlert);
  const skin = currentUser?.userSkin !== undefined && currentUser?.userSkin;

  // START Amplitude setup
  const identifyAmplitudeUser = () => {
    if (!currentUser?.id) return;

    // Create an Identify instance to set Amplitude user properties
    const identifyUser = new Identify();

    // Set multiple user properties
    identifyUser.set('admin', currentUser?.admin);
    identifyUser.set('gender', currentUser?.gender);
    identifyUser.set('organizationAccessLevel', currentUser?.organizationAccessLevel);
    identifyUser.set('organizationId', currentUser?.organizationId);
    identifyUser.set('organizationName', currentUser?.organizationName);
    identifyUser.set('organizationResellerAccountEnabled', currentUser?.organizationResellerAccountEnabled);
    identifyUser.set('organizationStripeSubscriptionActive', currentUser?.organizationStripeSubscriptionActive);
    identifyUser.set('organizationSubscriptionStatus', currentUser?.organizationSubscriptionStatus);
    identifyUser.set('primaryAdmin', currentUser?.primaryAdmin);
    identifyUser.set('ssHubEnabled', currentUser?.userOrgSSHubSettings?.ssHubEnabled);

    // Set the Amplitude user properties
    identify(identifyUser);
  };

  const initializeAmplitude = (isClientAlreadyInitialized) => {
    const isAmplitudeFlagEnabled = flags && flags.webAmplitude;

    if (isAmplitudeFlagEnabled) {
      if (!isClientAlreadyInitialized) {
        // Initialize Amplitude
        init('153704185f16943e3f8609c338cdb899', {
          defaultTracking: true,
          minIdLength: 1,
          userId: currentUser?.id,
        });

        isAmplitudeInitialized.current = true;
      }

      identifyAmplitudeUser();
    }
  };

  useEffect(() => {
    // Only allow Amplitude to initialize in production and after a user is logged in.
    if (process.env.ENVIRONMENT === 'PRODUCTION' && currentUser?.id && flags) {
      initializeAmplitude(isAmplitudeInitialized.current);
    }
  }, [currentUser, flags]);
  // END Amplitude setup

  const theme = {
    colorAccent: skin && skin.accent,
    colorPrimary: skin && skin.primary,
    colorSecondary: skin && skin.secondary,
    isDarkMode: skin && skin.darknessMode,
    isAccentDarkTheme: skin && skin.accentDarkTheme,
    isPrimaryDarkTheme: skin && skin.darkTheme,
    isSecondaryDarkTheme: skin && skin.secondaryDarkTheme,
    ...mainTheme,
  };

  const StyledToastContainer = styled(ToastContainer)`
  &&&.Toastify__toast-container {
  }
  .Toastify__toast {
  }
  .Toastify__toast-body {
    font-family: 'Nunito Sans';
    color: white;
  }
  .Toastify__close-button {
    color: white;
    opacity: 1;
    :hover {
      opacity: 0.7;
    }
  }
  .Toastify__close-button > svg {
    color: white;
  }
  .Toastify__toast-theme--light {
    background: #444444;
  }
`;

  useEffect(() => {
    if (showMaintenanceModeAlert) {
      // TODO: potentially create a nicer banner component to show this message
      const alertMessage = 'TeamBuildr is currently under maintenance. Please visit https://status.teambuildr.com to check the current status of TeamBuildr servers.';
      alert(alertMessage);
    }
  }, [showMaintenanceModeAlert]);

  useEffect(() => {
    if (currentUser && Object.keys(currentUser).length && ldClient) {
      ldClient.identify({
        kind: 'user',
        key: currentUser.id,
        name: `${currentUser.first} ${currentUser.last}`,
        email: currentUser.email,
        custom: {
          accountCode: currentUser.accountCode,
          platform: 'web',
        },
      });
    }
  }, [currentUser, ldClient]);

  // Load ContentSquare script for trial admins
  useEffect(() => {
    if (currentUser?.organizationSubscriptionStatus === 'IN_TRIAL' && currentUser?.admin === 1 && !isContentSquareLoaded.current) {
      // Add ContentSquare tracking script
      const contentSquareScript = document.createElement('script');
      contentSquareScript.src = 'https://t.contentsquare.net/uxa/91be1269c2b06.js';
      contentSquareScript.async = true;
      document.head.appendChild(contentSquareScript);

      console.log('ContentSquare script loaded');
      // Set the useRef to true to prevent re-initialization
      isContentSquareLoaded.current = true;
    }
    console.log('ContentSquare script not loaded');
  }, [currentUser]);

  return (
    <ThemeProvider theme={theme}>
      <ErrorBoundaryWrapper fallback={<FourOhFourRoute errorBoundary />}>
        <div className='custom-toast-container' style={{ position: 'absolute' }}>
          <StyledToastContainer
            position='bottom-right'
            autoClose={5000}
            closeOnClick
            pauseOnHover
            theme='dark'
          />
        </div>
        <AppRoutes />
      </ErrorBoundaryWrapper>
    </ThemeProvider>
  );
};

AppWrapper.propTypes = {
  flags: PropTypes.shape({
    webAmplitude: PropTypes.bool,
  }),
  ldClient: PropTypes.shape({
    identify: PropTypes.func,
  }),
};

AppWrapper.defaultProps = {
  flags: {},
  ldClient: null,
};

export default withLDConsumer()(AppWrapper);
